import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import getDay from "date-fns/getDay";
import format from "date-fns/format";
import eachDayOfInterval from "date-fns/eachDayOfInterval";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from "react-datepicker";
import de from "date-fns/locale/de";
import { useTranslation } from "react-i18next";
import styles from "@/components/appointmentRequest/dateTimePicker.module.css";
import inlineCalendarStyles from "@/components/dateTime/genericInlineCalendar.module.css";

registerLocale("de", de);

interface Props {
  onlyWeekDays?: boolean;
  handleDateRange?: (item: any) => void;
  filterDate?(date: Date): boolean;
  selectsRange?: boolean;
  initialDateRange?: [Date | null, Date | null];
  calculatedDays: any;
  selectedDays: any;
  disabled?: boolean;
}

export function DateTimePicker(props: Props) {
  const { t, i18n } = useTranslation();
  const initialStartDate = props.initialDateRange?.[0] ?? null;
  const initialEndDate = props.initialDateRange?.[1] ?? null;
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    initialStartDate,
    initialEndDate,
  ]);
  const [startDate, endDate] = dateRange;
  const calendarLocale = i18n.language === "de" ? "de" : undefined;

  useEffect(() => {
    setDateRange([initialStartDate, initialEndDate]);
  }, [initialStartDate, initialEndDate]);

  //disables Weekends in the calendar
  const isWeekday = (date: any) => {
    const day = getDay(date);
    return day !== 0 && day !== 6;
  };

  const eachDayOfRange =
    startDate &&
    endDate &&
    eachDayOfInterval({ start: startDate, end: endDate });

  let appointmentRequestDays: any;
  if (props.onlyWeekDays) {
    appointmentRequestDays =
      eachDayOfRange !== null &&
      eachDayOfRange?.filter((x: any) => isWeekday(x));
  } else {
    appointmentRequestDays = eachDayOfRange;
  }

  const updateRange = (update: any) => {
    setDateRange(update);
  };

  useEffect(() => {
    if (props.handleDateRange) {
      if (startDate && !endDate) {
        props.handleDateRange(startDate);
      }
      if (startDate && endDate) {
        props.handleDateRange(appointmentRequestDays);
      }
    }
  }, [startDate, endDate]);

  return (
    <div
      className={inlineCalendarStyles.surface}
      data-testid="appointment-request-date-range"
    >
      <DatePicker
        selectsRange={true}
        onChange={(update) => updateRange(update)}
        locale={calendarLocale}
        calendarStartDay={1}
        startDate={startDate}
        endDate={endDate}
        filterDate={props.filterDate}
        calendarClassName={[
          inlineCalendarStyles.calendar,
          inlineCalendarStyles.appointmentRequestCalendar,
          inlineCalendarStyles.appointmentRequestRangeCalendar,
        ].join(" ")}
        inline={!props.disabled}
        disabled={props.disabled}
        renderDayContents={(day, date) =>
          date ? (
            <span
              data-testid={`appointment-request-calendar-day-${format(
                date,
                "yyyy-MM-dd"
              )}`}
            >
              {day}
            </span>
          ) : (
            day
          )
        }
      >
        <div className={styles.datePickerWrapper}>
          <p>
            {t("required")}:{" "}
            <span className={styles.datePickerText}>{props.calculatedDays}</span>
          </p>
          <p>
            {t("selected")}:
            <span className={styles.datePickerText}>{props.selectedDays}</span>
          </p>
        </div>
      </DatePicker>
    </div>
  );
}
